import type { IteroOrder, IteroPatient } from '../../../../../services/iteroBrowse';
import { formatFullName, genderLabel, orEmpty } from '../iteroBrowseFormat.ts';

export interface PaneIdentity {
    name: string;
    chartNumber: string | null;
    dateOfBirth: string | null;
    gender: string | null;
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

interface CalendarDay {
    year: number;
    month: number;
    day: number;
}

function calendarDay(value: string | null): CalendarDay | null {
    const match = value ? ISO_DAY.exec(value) : null;
    if (!match) return null;
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

// Whole years on a local calendar day, so a birthday counts from its morning in every timezone.
export function ageOn(dateOfBirth: string | null, today: string): number | null {
    const birth = calendarDay(dateOfBirth);
    const now = calendarDay(today);
    if (!birth || !now) return null;
    const hadBirthday =
        now.month > birth.month || (now.month === birth.month && now.day >= birth.day);
    const age = now.year - birth.year - (hadBirthday ? 0 : 1);
    return age >= 0 ? age : null;
}

export function ageLabel(age: number | null): string | null {
    if (age === null) return null;
    return age === 1 ? '1 year' : `${age} years`;
}

export function identitySubline(identity: PaneIdentity, today: string): string {
    return [
        identity.chartNumber,
        ageLabel(ageOn(identity.dateOfBirth, today)),
        genderLabel(identity.gender)
    ]
        .filter((part): part is string => part !== null)
        .join(' · ');
}

export function identityFromPatient(patient: IteroPatient): PaneIdentity {
    return {
        name: formatFullName(patient.firstName, patient.lastName),
        chartNumber: orEmpty(patient.chartNumber),
        dateOfBirth: orEmpty(patient.dateOfBirth),
        gender: orEmpty(patient.gender)
    };
}

// An order carries no date of birth or gender, so its bar shows name and chart only.
export function identityFromOrder(order: IteroOrder): PaneIdentity {
    return {
        name: formatFullName(order.patientFirstName, order.patientLastName),
        chartNumber: orEmpty(order.patientChartNumber),
        dateOfBirth: null,
        gender: null
    };
}
